import Analytics, { AnalyticsInstance } from "analytics";
import React, { createContext, useContext, useEffect, useState } from "react";
// @ts-ignore - No types available for this package
import googleAnalytics from "@analytics/google-analytics";

interface MetaPixelConfig {
  pixelId: string;
  options?: {
    autoConfig?: boolean;
    debug?: boolean;
  };
}

// Custom Meta Pixel plugin for DavidWells analytics
function metaPixelPlugin({ pixelId, options }: MetaPixelConfig) {
  return {
    name: "meta-pixel",
    config: {
      options,
      pixelId,
    },
    initialize: ({ config }: { config: MetaPixelConfig }) => {
      const { pixelId, options } = config;
      if (!pixelId) return undefined;

      // Load Facebook Pixel script
      (function (
        f: any,
        b: Document,
        e: string,
        v: string,
        n?: any,
        t?: HTMLScriptElement,
        s?: Element
      ) {
        if (f.fbq) return;
        n = f.fbq = function () {
          if (n?.callMethod) {
            n.callMethod.apply(n, arguments);
          } else {
            n?.queue?.push(arguments);
          }
        };
        if (!f._fbq) f._fbq = n;
        if (n) {
          n.push = n;
          n.loaded = true;
          n.version = "2.0";
          n.queue = [];
        }
        t = b.createElement(e) as HTMLScriptElement;
        if (t) {
          t.async = true;
          t.src = v;
        }
        s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode && t) {
          s.parentNode.insertBefore(t, s);
        }
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );

      // Initialize pixel
      (window as any).fbq("init", pixelId, options);
      (window as any).fbq("track", "PageView");
    },

    page: () => {
      if (typeof (window as any).fbq !== "undefined") {
        (window as any).fbq("track", "PageView");
      }
    },

    track: ({ payload }: { payload: any }) => {
      if (typeof (window as any).fbq !== "undefined") {
        const { event, properties } = payload;

        (window as any).fbq("track", event, properties);
      }
    },

    identify: () => {},

    loaded: () => typeof (window as any).fbq !== "undefined",
  };
}

// TikTok Pixel plugin for DavidWells analytics
interface TikTokPixelConfig {
  pixelId: string;
  options?: Record<string, any>;
}

function tiktokPixelPlugin({ pixelId, options }: TikTokPixelConfig) {
  return {
    name: "tiktok-pixel",
    config: {
      options,
      pixelId,
    },
    initialize: ({ config }: { config: TikTokPixelConfig }) => {
      const { pixelId, options } = config;
      if (!pixelId) return undefined;

      // Load TikTok Pixel script and initialize
      (function (w: any, d: Document, t: string) {
        w.TiktokAnalyticsObject = t;
        const ttq = (w[t] = w[t] || []);
        ttq.methods = [
          "page",
          "track",
          "identify",
          "instances",
          "debug",
          "on",
          "off",
          "once",
          "ready",
          "alias",
          "group",
          "enableCookie",
          "disableCookie",
        ];
        ttq.setAndDefer = function (tObj: any, e: string) {
          tObj[e] = function () {
            tObj.push([e].concat(Array.prototype.slice.call(arguments, 0)));
          };
        };
        for (let i = 0; i < ttq.methods.length; i++) {
          ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        ttq.instance = function (tId: string) {
          const e = ttq._i[tId] || [];
          for (let n = 0; n < ttq.methods.length; n++) {
            ttq.setAndDefer(e, ttq.methods[n]);
          }
          return e;
        };
        ttq.load = function (e: string, n?: Record<string, any>) {
          const i = "https://analytics.tiktok.com/i18n/pixel/events.js";
          ttq._i = ttq._i || {};
          ttq._i[e] = [];
          ttq._i[e]._u = i;
          ttq._t = ttq._t || {};
          ttq._t[e] = +new Date();
          ttq._o = ttq._o || {};
          ttq._o[e] = n || {};
          const o = d.createElement("script");
          o.type = "text/javascript";
          (o as any).async = true;
          (o as any).src = i;
          const a = d.getElementsByTagName("script")[0];
          a.parentNode?.insertBefore(o, a);
        };
      })(window as any, document, "ttq");

      (window as any).ttq.load(pixelId, options || {});
      (window as any).ttq.page();
    },

    page: () => {
      if (typeof (window as any).ttq !== "undefined") {
        (window as any).ttq.page();
      }
    },

    track: ({ payload }: { payload: any }) => {
      if (typeof (window as any).ttq !== "undefined") {
        const { event, properties } = payload;
        const mapEvent = (name: string) => {
          switch (name) {
            case "Product Viewed":
              return "ViewContent";
            case "Product Added":
              return "AddToCart";
            case "Checkout Started":
              return "InitiateCheckout";
            case "Order Completed":
              return "CompletePayment";
            default:
              return name;
          }
        };
        const mappedEvent = mapEvent(event);
        (window as any).ttq.track(mappedEvent, properties);
      }
    },

    identify: ({ payload }: { payload: any }) => {
      if (typeof (window as any).ttq !== "undefined") {
        const { userId, traits } = payload || {};
        if ((window as any).ttq.identify) {
          (window as any).ttq.identify({ userId, ...traits });
        }
      }
    },

    loaded: () => typeof (window as any).ttq !== "undefined",
  };
}

interface AnalyticsContextType {
  analytics: AnalyticsInstance | null;
  isInitialized: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  analytics: null,
  isInitialized: false,
});

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsInstance | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Read analytics IDs from DOM (injected by Cloudflare Worker)
    const getAnalyticsConfig = () => {
      // Try to get from meta tags first
      const gaId = document
        .querySelector('meta[name="google-analytics-id"]')
        ?.getAttribute("content");
      const fbPixelId = document
        .querySelector('meta[name="meta-pixel-id"]')
        ?.getAttribute("content");
      const ttPixelId = document
        .querySelector('meta[name="tiktok-pixel-id"]')
        ?.getAttribute("content");

      return {
        googleAnalyticsId: gaId,
        metaPixelId: fbPixelId,
        tiktokPixelId: ttPixelId,
      };
    };

    const initializeAnalytics = () => {
      const { googleAnalyticsId, metaPixelId, tiktokPixelId } =
        getAnalyticsConfig();

      const plugins: any[] = [];

      // Add Google Analytics plugin if ID is available
      if (googleAnalyticsId) {
        plugins.push(
          googleAnalytics({
            measurementIds: [googleAnalyticsId],
          })
        );
      }

      // Add Meta Pixel plugin if ID is available
      if (metaPixelId) {
        plugins.push(
          metaPixelPlugin({
            pixelId: metaPixelId,
            options: {
              autoConfig: true,
            },
          })
        );
      }

      // Add TikTok Pixel plugin if ID is available
      if (tiktokPixelId) {
        plugins.push(
          tiktokPixelPlugin({
            pixelId: tiktokPixelId,
            options: {},
          })
        );
      }

      // Only initialize if we have at least one plugin
      if (plugins.length > 0) {
        const analyticsInstance = Analytics({
          app: "retail-storefront",
          plugins,
        });

        setAnalytics(analyticsInstance);
        setIsInitialized(true);

        console.log(
          "Analytics initialized with plugins:",
          plugins.map((p) => p.name || "meta-pixel")
        );
      } else {
        console.warn(
          "No analytics IDs found in DOM. Analytics not initialized."
        );
      }
    };

    // Initialize immediately if DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initializeAnalytics);
    } else {
      initializeAnalytics();
    }

    return () => {
      document.removeEventListener("DOMContentLoaded", initializeAnalytics);
    };
  }, []);

  return (
    <AnalyticsContext.Provider value={{ analytics, isInitialized }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
